import React from "react"
import { useTranslation } from "react-i18next"

import { NzDataGrid } from "@/components/nzDataGrid"
import { Page } from "@/components/page"
import { PageCard } from "@/components/page-card"
import { useActiveOrganization } from "@/contexts"
import {
  DEFAULT_PAGE_SIZE,
  DataGridFill,
  DataGridLegend,
  useEditRow,
  useInfiniteScroll,
  useSaveGridModel,
} from "@/nzds/data-grid"
import { useDepartmentsSpraypaint } from "@/services/department"
import { useGetBuildingTypes } from "@/services/site"
import { useAvailableReportDates } from "@/services/useAvailableReportDates"
import { useDateRange } from "@/utils/date/use-date-range"
import { usePage } from "@/utils/page/use-page"
import type { Interval } from "luxon"

import { Box } from "@mui/material"
import { useGridApiRef } from "@mui/x-data-grid-pro"
import type { GridSlotProps, GridSlots } from "@mui/x-data-grid-pro"

import type {
  SiteExplorerGridRecord,
  SiteExplorerRecord,
} from "../../services/use-get-site-explorer"
import { useGetSiteExplorer } from "../../services/use-get-site-explorer"
import { useComputedSiteRows } from "../utils"
import { useHandleProcessRowUpdate } from "../utils/use-handle-process-row-update"
import { SitesOverviewGridToolbar } from "./sites-overview-grid-toolbar"
import {
  defaultColumnVisibilityModel,
  defaultPinnedColumns,
  defaultSortModel,
  useOverviewGridColumns,
} from "./utils/use-overview-grid-columns/use-overview-grid-columns"

export const SitesOverviewRoute: React.FC = () => {
  const { t } = useTranslation()
  const gridApiRef = useGridApiRef()

  const { activeOrganization } = useActiveOrganization()
  const { availableReportDatesData } = useAvailableReportDates()

  const { buildingTypesData, buildingTypesIsFetching } = useGetBuildingTypes()
  const { departmentsSpraypaintData, departmentsSpraypaintIsFetching } =
    useDepartmentsSpraypaint({
      apiQueryOptions: {
        where: { organization_id: activeOrganization?.id },
      },
    })

  const { page } = usePage({
    id: "sitesOverviewPage",
  })

  const { dateRange, setDateRange } = useDateRange({
    id: "sitesOverviewDateRange",
    bounds: availableReportDatesData,
  })

  const handleDateRangeChange = (value: Interval | null) => {
    if (value?.isValid) {
      setDateRange(value)
    }
  }

  const {
    handleRowEditCancelClick,
    handleRowEditClick,
    handleRowEditSaveClick,
    handleRowEditStop,
    rowModesModel,
  } = useEditRow()

  const defaultColumns = useOverviewGridColumns({
    buildingTypes: buildingTypesData,
    departments: departmentsSpraypaintData,
    handleRowEditCancelClick,
    handleRowEditClick,
    handleRowEditSaveClick,
    rowModesModel,
  })

  const {
    gridModel: { columnVisibilityModel, columns, pinnedColumns, sortModel },
    handleColumnOrderChange,
    handleColumnVisibilityModelChange,
    handlePinnedColumnsChange,
    handleSortModelChange,
  } = useSaveGridModel<SiteExplorerGridRecord>({
    gridName: "sitesOverview",
    gridModel: {
      columns: defaultColumns,
      columnVisibilityModel: defaultColumnVisibilityModel,
      pinnedColumns: defaultPinnedColumns,
      sortModel: defaultSortModel,
    },
  })

  const isSiteExplorerQueryEnabled =
    Boolean(activeOrganization?.id) &&
    Boolean(sortModel[0].field) &&
    Boolean(sortModel[0].sort) &&
    dateRange.isValid

  const {
    siteExplorerData,
    siteExplorerFetchNextPage,
    siteExplorerHasNextPage,
    siteExplorerIsFetching,
    siteExplorerIsFetchingNextPage,
  } = useGetSiteExplorer({
    parameters: {
      query: {
        "filter[organization_id]": activeOrganization?.id,
        "filter[start_date][gte]": dateRange.start?.toISODate(),
        "filter[end_date][lte]": dateRange.end?.toISODate(),
        "page[size]": DEFAULT_PAGE_SIZE,
        sort: `${sortModel[0].sort === "asc" ? "" : "-"}${sortModel[0].field}`,
        "stats[page]": "count",
      },
    },
    initialPage: page,
    queryOptions: {
      enabled: isSiteExplorerQueryEnabled,
    },
  })

  const rows = useComputedSiteRows<SiteExplorerRecord, SiteExplorerGridRecord>({
    data: siteExplorerData,
  })

  const { handleOnRowsScrollEnd } = useInfiniteScroll({
    fetchNextPage: siteExplorerFetchNextPage,
    hasNextPage: siteExplorerHasNextPage,
    isEnabled: isSiteExplorerQueryEnabled,
    isFetchingNextPage: siteExplorerIsFetchingNextPage,
  })

  const { handleProcessRowUpdate } = useHandleProcessRowUpdate(
    departmentsSpraypaintData
  )

  const handleProcessRowUpdateError = React.useCallback((error: Error) => {
    console.error(error)
  }, [])

  return (
    <Page fullHeight>
      <PageCard
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        <Box
          sx={{
            mb: 3,
          }}
        >
          <DataGridLegend
            itemCount={siteExplorerData?.pages[0]?.total}
            itemLabel={t("siteCountLabel", { ns: "sites" })}
          />
        </Box>
        <DataGridFill>
          <NzDataGrid
            apiRef={gridApiRef}
            aria-label="Sites overview"
            columns={columns}
            columnVisibilityModel={columnVisibilityModel}
            disableColumnFilter
            loading={
              buildingTypesIsFetching ||
              departmentsSpraypaintIsFetching ||
              siteExplorerIsFetching ||
              siteExplorerIsFetchingNextPage
            }
            onColumnOrderChange={handleColumnOrderChange}
            onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
            onPinnedColumnsChange={handlePinnedColumnsChange}
            onProcessRowUpdateError={handleProcessRowUpdateError}
            onRowEditStop={handleRowEditStop}
            onRowsScrollEnd={handleOnRowsScrollEnd}
            onSortModelChange={handleSortModelChange}
            pinnedColumns={pinnedColumns}
            processRowUpdate={handleProcessRowUpdate}
            rowModesModel={rowModesModel}
            rows={rows}
            slotProps={{
              basePopper: {
                placement: "bottom-start",
              },
              toolbar: {
                availableMaxMinMonths: availableReportDatesData,
                dateRange,
                onDateRangeChange: handleDateRangeChange,
                organizationId: activeOrganization?.id,
              } as GridSlotProps["toolbar"],
            }}
            slots={{
              toolbar: SitesOverviewGridToolbar as GridSlots["toolbar"],
            }}
            sortingMode="server"
            sortModel={sortModel}
          />
        </DataGridFill>
      </PageCard>
    </Page>
  )
}
